/*
 * Ячейки и карточки раздела JupyterHub: серверы пользователей хаба кластера
 * (бэкенд-плагин jupyterhub, сервис backstage хаба — только чтение).
 */
import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusOK,
  StatusPending,
  StatusWarning,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import useAsync from 'react-use/esm/useAsync';
import { DATASTORE_CLUSTER_ANNOTATION, annotation } from '../../lib/viva';
import { JupyterServer, jupyterhubApiRef } from '../../lib/jupyterhub';
import { AsyncCell } from '../catalog/cells';

const since = (iso: string | null): string => {
  if (!iso) return '—';
  const minutes = Math.max(
    0,
    Math.round((Date.now() - Date.parse(iso)) / 60000),
  );
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  return hours < 48
    ? `${hours} ч ${minutes % 60} мин`
    : `${Math.floor(hours / 24)} дн`;
};

const ServerStatus = ({ server }: { server: JupyterServer }) => {
  if (server.ready) return <StatusOK>работает</StatusOK>;
  if (server.pending) {
    return (
      <StatusPending>
        {server.pending === 'spawn' ? 'запускается' : server.pending}
      </StatusPending>
    );
  }
  return <StatusWarning>не готов</StatusWarning>;
};

export const useHubUsers = (entity: Entity) => {
  const api = useApi(jupyterhubApiRef);
  const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
  return useAsync(async () => {
    if (!cluster)
      throw new Error(`нет аннотации ${DATASTORE_CLUSTER_ANNOTATION}`);
    return api.getUsers(cluster);
  }, [cluster, api]);
};

/** Ячейка таблицы раздела: «N серверов, M запускается · K польз.». */
export const EntityHubServersCell = ({ entity }: { entity: Entity }) => {
  const state = useHubUsers(entity);
  return (
    <AsyncCell state={state} unavailable="хаб недоступен">
      {data => {
        const { running, pending, users } = data.summary;
        const label = `${running} ${running === 1 ? 'сервер' : 'серверов'}${
          pending ? `, ${pending} запускается` : ''
        } · ${users} польз.`;
        const who =
          data.servers
            .map(s => `${s.user}${s.name ? `/${s.name}` : ''}`)
            .join(', ') || 'запущенных серверов нет';
        return (
          <Tooltip title={who}>
            <Typography variant="body2" noWrap>
              {running > 0 ? (
                <StatusOK>{label}</StatusOK>
              ) : (
                <span>{label}</span>
              )}
            </Typography>
          </Tooltip>
        );
      }}
    </AsyncCell>
  );
};

const columns: TableColumn<JupyterServer>[] = [
  { title: 'Пользователь', field: 'user', highlight: true },
  { title: 'Сервер', render: s => s.name || 'по умолчанию' },
  { title: 'Состояние', render: s => <ServerStatus server={s} /> },
  { title: 'Профиль', render: s => s.profile ?? '—' },
  {
    title: 'Запущен',
    render: s => (s.started ? `${since(s.started)} назад` : '—'),
  },
  {
    title: 'Активность',
    render: s => (s.lastActivity ? `${since(s.lastActivity)} назад` : '—'),
  },
];

/** Карточка страницы сущности jupyterhub-<кластер>: кто и с какого времени работает. */
export const EntityHubServersCard = () => {
  const { entity } = useEntity();
  const state = useHubUsers(entity);
  const hubLink = entity.metadata.links?.find(
    l => l.title === 'JupyterHub',
  )?.url;
  if (state.loading) {
    return (
      <InfoCard title="Серверы пользователей">
        <Progress />
      </InfoCard>
    );
  }
  if (state.error) {
    return (
      <InfoCard title="Серверы пользователей">
        <ResponseErrorPanel error={state.error} />
      </InfoCard>
    );
  }
  const data = state.value!;
  const subheader = `${data.summary.users} пользователей вошли в хаб · ${
    data.summary.running
  } серверов работает${
    data.summary.pending ? ` · ${data.summary.pending} запускается` : ''
  }`;
  return (
    <InfoCard
      title="Серверы пользователей"
      subheader={subheader}
      action={
        hubLink ? <Link to={hubLink}>Открыть JupyterHub</Link> : undefined
      }
    >
      {data.servers.length === 0 ? (
        <Box p={1}>
          <Typography variant="body2" color="textSecondary">
            Запущенных серверов нет. Сервер простаивает час — хаб его гасит,
            домашний том пользователя остаётся.
          </Typography>
        </Box>
      ) : (
        <Table
          options={{
            paging: data.servers.length > 10,
            search: false,
            toolbar: false,
            padding: 'dense',
          }}
          data={data.servers}
          columns={columns}
        />
      )}
    </InfoCard>
  );
};
